import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { captureException } from "@/lib/sentry";

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

/**
 * Capture les crashs de rendu d'une page OPS pour que la sidebar et le reste
 * de l'app survivent (au lieu d'un écran blanc total — cf. AOP-8571).
 *
 * Placé à l'intérieur d'AdminLayout autour de {children} et remonté à chaque
 * changement de route (key={location.pathname}) pour se réinitialiser
 * automatiquement quand l'opérateur navigue vers une autre page.
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Visible en console pour le diagnostic ; l'UI reste utilisable.
    console.error("[OPS] Crash de page capturé par RouteErrorBoundary", error, info);
    // Forward the render crash to Sentry with the current route + component stack.
    captureException(error, {
      contexts: {
        react: { componentStack: info.componentStack },
        location: { pathname: window.location.pathname },
      },
    });
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
            <AlertTriangle className="h-8 w-8" />
          </div>
          <h2 className="text-lg font-semibold mb-1">Cette page a rencontré une erreur</h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md">
            Le reste de l'application reste accessible via le menu. Vous pouvez réessayer
            d'afficher cette page ou naviguer ailleurs.
          </p>
          <pre className="mb-6 max-w-lg overflow-x-auto rounded-md bg-muted px-3 py-2 text-left text-xs text-muted-foreground">
            {this.state.error.message}
          </pre>
          <Button variant="outline" onClick={this.handleReset}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Réessayer
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
